
import { useState, useEffect } from 'react';
import { useReviews } from './useReviews';

export interface EnhancedReview {
  id: number;
  patientName: string;
  patientImage?: string;
  rating: number;
  date: string;
  comment: string;
  treatmentType?: string;
  doctorReply?: string;
  language?: string;
  translation?: string;
  isPinned?: boolean;
  keywords?: {
    text: string;
    sentiment: 'positive' | 'negative' | 'neutral';
  }[];
}

export interface EvaluationQuestion {
  id: string;
  text: string;
  averageRating: number;
  count: number;
}

export interface ServiceEvaluation {
  serviceName: string;
  questions: EvaluationQuestion[];
}

export interface MonthlyReviewData {
  month: string;
  count: number;
  avgRating: number;
}

interface UseEnhancedReviewsReturn {
  reviews: EnhancedReview[];
  pinnedReviewId: number | null;
  reviewTrends: MonthlyReviewData[];
  evaluations: ServiceEvaluation[];
  isLoading: boolean;
  error: string | null;
  replyToReview: (reviewId: number, reply: string) => void;
  pinReview: (reviewId: number) => void;
  translateReview: (reviewId: number) => void;
  requestReview: (patientId: number, service: string) => void;
}

// Some sample positive and negative keywords for sentiment analysis
const positiveKeywords = ['excellent', 'great', 'good', 'amazing', 'helpful', 'professional', 'comfortable', 'recommend', 'friendly', 'skilled'];
const negativeKeywords = ['bad', 'poor', 'painful', 'waiting', 'expensive', 'rude', 'unprofessional', 'disappointing', 'worst', 'confusing'];

export const useEnhancedReviews = (): UseEnhancedReviewsReturn => {
  // Get basic reviews data
  const { reviews: basicReviews, metrics, loading, error } = useReviews();
  const [enhancedReviews, setEnhancedReviews] = useState<EnhancedReview[]>([]);
  const [pinnedReviewId, setPinnedReviewId] = useState<number | null>(null);
  
  // Sample monthly trend data
  const [reviewTrends, setReviewTrends] = useState<MonthlyReviewData[]>([
    { month: 'Oct', count: 3, avgRating: 4.7 },
    { month: 'Nov', count: 5, avgRating: 4.4 },
    { month: 'Dec', count: 4, avgRating: 4.2 },
    { month: 'Jan', count: 7, avgRating: 4.5 },
    { month: 'Feb', count: 5, avgRating: 4.3 },
    { month: 'Mar', count: 8, avgRating: 4.6 },
    { month: 'Apr', count: 6, avgRating: 4.4 },
    { month: 'May', count: 9, avgRating: 4.7 },
    { month: 'Jun', count: 7, avgRating: 4.5 },
    { month: 'Jul', count: 8, avgRating: 4.6 },
    { month: 'Aug', count: 10, avgRating: 4.8 },
    { month: 'Sep', count: 12, avgRating: 4.7 },
  ]);
  
  // Sample evaluation questions by service
  const [evaluations, setEvaluations] = useState<ServiceEvaluation[]>([
    {
      serviceName: 'Dental Implant',
      questions: [
        { id: 'q1', text: 'How would you rate the pain management?', averageRating: 4.2, count: 15 },
        { id: 'q2', text: 'Was the doctor clear in communication?', averageRating: 3.5, count: 15 },
        { id: 'q3', text: 'How satisfied are you with the results?', averageRating: 4.7, count: 15 },
        { id: 'q4', text: 'How was the overall experience?', averageRating: 4.3, count: 15 },
      ]
    },
    {
      serviceName: 'Teeth Whitening',
      questions: [
        { id: 'q5', text: 'How satisfied are you with the whitening results?', averageRating: 4.5, count: 12 },
        { id: 'q6', text: 'Did the doctor provide clear aftercare instructions?', averageRating: 4.8, count: 12 },
        { id: 'q7', text: 'How was the overall experience?', averageRating: 4.6, count: 12 },
      ]
    },
    {
      serviceName: 'Root Canal',
      questions: [
        { id: 'q8', text: 'How would you rate the pain management?', averageRating: 3.9, count: 8 },
        { id: 'q9', text: 'Was the procedure explained clearly?', averageRating: 4.1, count: 8 },
        { id: 'q10', text: 'How was your comfort level during the procedure?', averageRating: 3.7, count: 8 },
        { id: 'q11', text: 'How would you rate the post-procedure care?', averageRating: 4.2, count: 8 },
      ]
    }
  ]);

  useEffect(() => {
    if (!loading && basicReviews.length > 0) {
      // Transform basic reviews to enhanced reviews
      const enhanced: EnhancedReview[] = basicReviews.map(review => {
        // Extract keywords for sentiment analysis
        const words = review.comment.toLowerCase().split(/\s+/);
        const keywords = words
          .filter(word => 
            positiveKeywords.includes(word) || 
            negativeKeywords.includes(word)
          )
          .map(word => ({
            text: word,
            sentiment: positiveKeywords.includes(word) ? 'positive' as const : 'negative' as const
          }));
        
        // Random language for some reviews to demonstrate translation feature
        const languages = ['en', 'es', 'fr'];
        const randomLang = Math.random() > 0.7 ? languages[Math.floor(Math.random() * 3)] : 'en';
        
        return {
          ...review,
          patientName: `Patient ${review.id}`,
          doctorReply: review.id === 1 ? "Thank you for your feedback! I'm glad you had a positive experience." : undefined,
          language: randomLang,
          translation: randomLang !== 'en' ? 'This is a translated text for demonstration purposes.' : undefined,
          keywords: keywords.length > 0 ? keywords : undefined,
        };
      });
      
      setEnhancedReviews(enhanced);
      
      // Set a random pinned review for demo
      if (enhanced.length > 0 && pinnedReviewId === null) {
        setPinnedReviewId(enhanced[0].id);
      }
    }
  }, [basicReviews, loading]);

  const replyToReview = (reviewId: number, reply: string) => {
    setEnhancedReviews(prev => 
      prev.map(review => 
        review.id === reviewId 
          ? { ...review, doctorReply: reply } 
          : review
      )
    );
  };

  const pinReview = (reviewId: number) => {
    if (pinnedReviewId === reviewId) {
      setPinnedReviewId(null);
    } else {
      setPinnedReviewId(reviewId);
    }
  };

  const translateReview = (reviewId: number) => {
    setEnhancedReviews(prev => 
      prev.map(review => 
        review.id === reviewId && review.language !== 'en' && !review.translation
          ? { ...review, translation: 'This is a translated version of the review text for demonstration purposes.' } 
          : review
      )
    );
  };

  const requestReview = (patientId: number, service: string) => {
    console.log(`Review requested for patient ${patientId} for service: ${service}`);
    // In a real implementation, this would send a notification to the patient
  };

  return {
    reviews: enhancedReviews,
    pinnedReviewId,
    reviewTrends,
    evaluations,
    isLoading: loading,
    error,
    replyToReview,
    pinReview,
    translateReview,
    requestReview
  };
};
